import * as React from 'react';

export const useToggle = (defaultValue: boolean = false) => {
	const [value, setValue] = React.useState(defaultValue);

	const setToTrue = React.useCallback(async () => setValue(true), []);
	const setToFalse = React.useCallback(async () => setValue(false), []);
	const toggle = React.useCallback(() => setValue((state) => !state), []);

	return { value, setToTrue, setToFalse, toggle };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useLazyLoad = <F extends (...args: any[]) => Promise<any>>(action: F, ...args: Parameters<F>) => {
	const [options, setValue] = React.useState<PromiseResult<ReturnType<F>>>([] as PromiseResult<ReturnType<F>>);

	const lazyLoad = React.useCallback(async (loaded: boolean) => {
		if (!loaded) {
			const _value = await action(...args);
			setValue(_value as PromiseResult<ReturnType<F>>);
		}
	}, [action, args]);

	return { options, lazyLoad };
};

/**
 * Lazy fetches data, but automatically invalidates the cached result when dependencies change,
 * forcing a fresh fetch on the next request.

 * @example
 * const loadData = async () => return await someFetch(filter, startDate, endDate);
 * const { options, lazyLoad } = useLazyLoadWithInvalidation(
 *   loadData,
 *   [filter, startDate, endDate]
 * );
 * // In your component:
 * <Dropdown onOpen={lazyLoad} options={options} />
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useLazyLoadWithInvalidation = <F extends (...args: any[]) => Promise<any>>(
	action: F,
	deps: React.DependencyList = []
) => {
	const [options, setValue] = React.useState<PromiseResult<ReturnType<F>>>([] as PromiseResult<ReturnType<F>>);
	const [loaded, setLoaded] = React.useState(false);

	// Reset loaded state when dependencies change
	React.useEffect(() => {
		setLoaded(false);
		setValue([] as PromiseResult<ReturnType<F>>);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, deps);

	const lazyLoad = React.useCallback(async () => {
		if (!loaded) {
			const _value = await action();
			setValue(_value as PromiseResult<ReturnType<F>>);
			setLoaded(true);
		}
	}, [action, loaded]);

	return { options, lazyLoad };
};

const DEFAULT_MIN_COLUMN_WIDTH = 50;

export interface CellWidthsMap {
	[id: string]: {
		width: number;
	};
}

type DraggableColumnsOptions = {
	minColumnWidth?: number;
	onColumnDragSideEffect?: (cellWidthMap?: CellWidthsMap) => void;
};

export const useDraggableColumns = (options: DraggableColumnsOptions) => {
	const { minColumnWidth = DEFAULT_MIN_COLUMN_WIDTH, onColumnDragSideEffect } = options;
	const [cellWidthsMap, setCellWidthsMap] = React.useState<CellWidthsMap>({});
	const draggingHeaderId = React.useRef<Nullable<string>>(null);
	const columnRefs = React.useRef<Record<string, HTMLSpanElement>>({});

	const onMouseMove = React.useCallback((e: MouseEvent) => {
		if (!draggingHeaderId.current || Object.keys(columnRefs.current).length === 0) {
			return;
		}
		const id = draggingHeaderId.current;
		const newCellWidthsMap = { ...cellWidthsMap };
		let newWidth = 0;
		if (cellWidthsMap[id]) {
			newWidth = newCellWidthsMap[id].width + e.movementX;
			if (newWidth < (minColumnWidth ?? DEFAULT_MIN_COLUMN_WIDTH)) {
				return;
			}
			newCellWidthsMap[id].width = newWidth;
		} else {
			const columnWidth = columnRefs.current[id].getBoundingClientRect().width;
			newWidth = +columnWidth + e.movementX;
			if (newWidth < (minColumnWidth ?? DEFAULT_MIN_COLUMN_WIDTH)) {
				return;
			}

			newCellWidthsMap[id] = { width: newWidth };
		}

		setCellWidthsMap(newCellWidthsMap);
		onColumnDragSideEffect?.(newCellWidthsMap);

	}, [cellWidthsMap, minColumnWidth, onColumnDragSideEffect]);

	const onColumnDrag = React.useCallback((header: string) => {
		return (e: React.MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();
			draggingHeaderId.current = header;
		};
	}, []);

	const onMouseUp = React.useCallback(() => {
		if (!draggingHeaderId.current) {
			return;
		}

		draggingHeaderId.current = null;
	}, []);

	React.useEffect(() => {
		document.addEventListener('mousemove', onMouseMove);
		document.addEventListener('mouseup', onMouseUp);

		return () => {
			document.removeEventListener('mousemove', onMouseMove);
			document.removeEventListener('mouseup', onMouseUp);
		};
	}, [onMouseMove, onMouseUp]);

	return {
		onColumnDrag,
		cellWidthsMap,
		columnRefs,
	};
};
